import { useEffect, useState } from 'react';
import { Sidebar } from '../components/Sidebar';
import { Status, Tag, statuses as statusesApi, tags as tagsApi } from '../lib/api';

const COLOR_PALETTE = [
  '#4A8C5E',
  '#5B9BD5',
  '#E8B547',
  '#E8896E',
  '#C77B92',
  '#C45656',
  '#8A6FB5',
  '#6A8A6A',
  '#8B8680',
];

interface EditState {
  id?: number;
  name: string;
  color: string;
  auto_tag_id: number | null;
}

export function StatusesPage() {
  const [statuses, setStatuses] = useState<Status[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [editing, setEditing] = useState<EditState | null>(null);
  const [loading, setLoading] = useState(true);

  function reload() {
    Promise.all([statusesApi.list(), tagsApi.list()])
      .then(([s, t]) => {
        setStatuses(s.statuses);
        setTags(t.tags);
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    reload();
  }, []);

  async function handleSave() {
    if (!editing) return;
    if (editing.id === undefined) {
      await statusesApi.create({
        name: editing.name,
        color: editing.color,
        auto_tag_id: editing.auto_tag_id,
      });
    } else {
      await statusesApi.update(editing.id, {
        name: editing.name,
        color: editing.color,
        auto_tag_id: editing.auto_tag_id,
      });
    }
    setEditing(null);
    reload();
  }

  async function handleDelete(id: number) {
    if (!confirm('このステータスを削除しますか？（友だちからは外れますが、友だちは削除されません）')) {
      return;
    }
    await statusesApi.remove(id);
    reload();
  }

  return (
    <div className="app-shell">
      <Sidebar />
      <main className="page">
        <div className="page-header">
          <div>
            <div className="page-title">対応ステータス管理</div>
            <div className="page-desc">
              友だちに付けるステータスを自由に編集できます。名前・色・自動付与タグの連動設定が可能です。
            </div>
          </div>
          <button
            className="btn btn-primary"
            onClick={() =>
              setEditing({ name: '', color: COLOR_PALETTE[0], auto_tag_id: null })
            }
          >
            + 新しいステータス
          </button>
        </div>

        <div className="info-callout">
          <b>ヒント：</b> 「自動付与タグ」を設定すると、ステータスを付けた瞬間にタグも自動でつきます。
          後で絞り込み配信に使えます。
        </div>

        {loading ? (
          <div className="loading">読み込み中...</div>
        ) : (
          <div className="table-card">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 60 }}>色</th>
                  <th>ステータス名</th>
                  <th>自動付与タグ</th>
                  <th style={{ textAlign: 'right', width: 90 }}>利用数</th>
                  <th style={{ width: 100 }}></th>
                </tr>
              </thead>
              <tbody>
                {statuses.map((s) => (
                  <tr key={s.id}>
                    <td>
                      <span className="color-chip" style={{ background: s.color }} />
                    </td>
                    <td style={{ fontWeight: 500, fontSize: 13 }}>{s.name}</td>
                    <td>
                      {s.auto_tag_name ? (
                        <span className="tag-pill">#{s.auto_tag_name}</span>
                      ) : (
                        <span style={{ color: 'var(--text-mute)', fontSize: 11 }}>なし</span>
                      )}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 500 }}>{s.usage_count}</td>
                    <td>
                      <div className="row-actions">
                        <button
                          className="icon-btn-sm"
                          onClick={() =>
                            setEditing({
                              id: s.id,
                              name: s.name,
                              color: s.color,
                              auto_tag_id: s.auto_tag_id,
                            })
                          }
                          title="編集"
                        >
                          ✎
                        </button>
                        <button
                          className="icon-btn-sm danger"
                          onClick={() => handleDelete(s.id)}
                          title="削除"
                        >
                          ✕
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {statuses.length === 0 && (
                  <tr>
                    <td colSpan={5} className="empty">
                      ステータスが登録されていません
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {editing && (
        <div className="modal-backdrop" onClick={() => setEditing(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-title">
              {editing.id === undefined ? 'ステータスを追加' : 'ステータスを編集'}
            </div>
            <div className="modal-sub">名前・色・連動タグを設定できます</div>

            <div className="field">
              <label className="field-label">ステータス名</label>
              <input
                className="input"
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                autoFocus
              />
            </div>

            <div className="field">
              <label className="field-label">色</label>
              <div className="color-picker">
                {COLOR_PALETTE.map((c) => (
                  <span
                    key={c}
                    className={`color-option ${editing.color === c ? 'selected' : ''}`}
                    style={{ background: c }}
                    onClick={() => setEditing({ ...editing, color: c })}
                  />
                ))}
              </div>
            </div>

            <div className="field">
              <label className="field-label">自動付与タグ（任意）</label>
              <select
                className="select"
                value={editing.auto_tag_id ?? ''}
                onChange={(e) =>
                  setEditing({
                    ...editing,
                    auto_tag_id: e.target.value === '' ? null : Number(e.target.value),
                  })
                }
              >
                <option value="">（連動なし）</option>
                {tags.map((t) => (
                  <option key={t.id} value={t.id}>
                    #{t.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setEditing(null)}>
                キャンセル
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={!editing.name.trim()}
              >
                保存
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
